import { Portal } from '@reach/portal';
import { TooltipPopup, useTooltip } from '@reach/tooltip';
import { useComposedRefs } from '@reach/utils';
import {
  type FC,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  cloneElement,
} from 'react';

import styles from './Tooltip.module.scss';

interface TooltipProps {
  label: ReactNode;
  type?: 'info' | 'dark-info' | 'warning';
  'aria-label': string;
  children: ReactElement;
  positioner?: (
    triggerRect?: PRect | null,
    tooltipRect?: PRect | null,
  ) =>
    | {
        left: number;
        top: number;
      }
    | {
        left?: undefined;
        top?: undefined;
      };
  size?: 'small' | 'large';
  show?: boolean;
  showTriangle?: boolean;
}

const Tooltip = ({
  ref: forwardedRef,
  children,
  label,
  'aria-label': ariaLabel,
  type = 'info',
  size = 'large',
  positioner = centered,
  show = true,
  showTriangle = true,
}: TooltipProps & {
  ref?: React.Ref<HTMLElement>;
}) => {
  // Get the props from useTooltip. It's important we pass any refs the children may have into this function. The tooltip
  // needs to attach it's own ref to the children and if we don't make it aware of the existing ref it will
  // overwrite it.
  const [trigger, tooltip] = useTooltip();
  const ref = useComposedRefs(trigger.ref, forwardedRef);

  // Destructure off what we need to position the triangle
  const { isVisible, triggerRect } = tooltip;
  const tooltipTriangleSize = size === 'small' ? 6 : 10;
  const tooltipTrianglePosition = triggerRect
    ? {
        left: triggerRect.left - tooltipTriangleSize + triggerRect.width / 2,
        top: triggerRect.bottom + window.scrollY,
      }
    : {};
  return (
    <>
      {cloneElement(children, { ...trigger, ref } as React.HTMLAttributes<HTMLElement> & {
        ref: typeof ref;
      })}
      {show && (
        <>
          {isVisible && showTriangle && (
            // The Triangle. We position it relative to the trigger, not the popup
            // so that collisions don't have a triangle pointing off to nowhere.
            // Using a Portal may seem a little extreme, but we can keep the
            // positioning logic simpler here instead of needing to consider
            // the popup's position relative to the trigger and collisions
            <Portal>
              <div
                className={styles['tooltip-triangle']}
                data-type={type}
                data-size={size}
                style={{
                  left: tooltipTrianglePosition.left,
                  top: tooltipTrianglePosition.top,
                }}
              />
            </Portal>
          )}
          <TooltipPopup
            {...tooltip}
            label={label}
            data-type={type}
            data-size={size}
            aria-label={ariaLabel}
            className={styles['tooltip']}
            position={positioner}
          />
        </>
      )}
    </>
  );
};

Tooltip.displayName = 'Tooltip';

interface RichTooltipLabelProps {
  label: string;
  subtext: string;
}

const RichTooltipLabel: FC<PropsWithChildren<RichTooltipLabelProps>> = ({ label, subtext }) => {
  return (
    <div className={styles['rich-tooltip-label']}>
      <div className={styles['rich-tooltip-label']}>{label}</div>
      <div className={styles['rich-tooltip-subtext']}>{subtext}</div>
    </div>
  );
};

export type PRect = Partial<DOMRect> & {
  readonly bottom: number;
  readonly height: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly width: number;
};

function centered(triggerRect?: PRect | null, tooltipRect?: PRect | null) {
  if (triggerRect && tooltipRect) {
    const SCREEN_PADDING = 4;
    const screenWidth = document.documentElement.clientWidth; // Width minus scrollbar
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const left = triggerCenter - tooltipRect.width / 2;
    const willCollide = left + tooltipRect.width > screenWidth;

    return {
      left: willCollide ? window.scrollX + screenWidth - tooltipRect.width - SCREEN_PADDING : left,
      top: triggerRect.bottom + 4 + window.scrollY,
    };
  }
  return {};
}

type CenteredAbovePosition =
  | {
      left: number;
      top: number;
    }
  | {
      left?: undefined;
      top?: undefined;
    };

// This needs to be fixed up and made more generic
// Quick fix to get feature out
export function centeredAbove(
  triggerRect?: PRect | null,
  tooltipRect?: PRect | null,
): CenteredAbovePosition {
  if (triggerRect && tooltipRect) {
    const SCREEN_PADDING = 4;
    const screenWidth = document.documentElement.clientWidth; // Width minus scrollbar
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const left = triggerCenter - tooltipRect.width / 2;
    const willCollide = left + tooltipRect.width > screenWidth;

    return {
      left: willCollide ? window.scrollX + screenWidth - tooltipRect.width - SCREEN_PADDING : left,
      top: triggerRect.top + window.scrollY - tooltipRect.height - 8,
    };
  }
  return {};
}

export default Tooltip;
export { RichTooltipLabel };
